import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db } from '../firebase';

export interface AdminDashboardStats {
  candidaturesCount: number;
  candidaturesPendingCount: number;
  schoolsCount: number;
  postsCount: number;
  activitiesValidatedCount: number;
  paymentsCount: number;
}

export interface CandidatureSummary {
  id: string;
  userId?: string;
  postId?: string;
  status?: string;
  date?: Date;
}

export interface ActivitySummary {
  id: string;
  candidatureId?: string;
  status?: string;
  date?: Date;
}

export interface PaymentSummary {
  id: string;
  activityId?: string;
  status?: string;
  amount?: number;
  createdAt?: Date;
}

const COLLECTIONS = {
  users: 'users',
  schools: 'schools',
  posts: 'posts',
  candidatures: 'candidatures',
  activities: 'activities',
  payments: 'payments',
} as const;

const STATUSES = {
  pending: 'pending',
  validated: 'validated',
  paid: 'paid',
} as const;

export async function fetchAdminDashboardStats(): Promise<AdminDashboardStats> {
  const [
    candidaturesSnap,
    candidaturesPendingSnap,
    schoolsSnap,
    postsSnap,
    activitiesValidatedSnap,
    paymentsSnap,
  ] = await Promise.all([
    getDocs(collection(db, COLLECTIONS.candidatures)),
    getDocs(
      query(
        collection(db, COLLECTIONS.candidatures),
        where('status', '==', STATUSES.pending)
      )
    ),
    getDocs(collection(db, COLLECTIONS.schools)),
    getDocs(collection(db, COLLECTIONS.posts)),
    getDocs(
      query(
        collection(db, COLLECTIONS.activities),
        where('status', '==', STATUSES.validated)
      )
    ),
    getDocs(collection(db, COLLECTIONS.payments)),
  ]);

  return {
    candidaturesCount: candidaturesSnap.size,
    candidaturesPendingCount: candidaturesPendingSnap.size,
    schoolsCount: schoolsSnap.size,
    postsCount: postsSnap.size,
    activitiesValidatedCount: activitiesValidatedSnap.size,
    paymentsCount: paymentsSnap.size,
  };
}

export async function fetchPendingCandidatures(
  limitCount = 10
): Promise<CandidatureSummary[]> {
  const snap = await getDocs(
    query(
      collection(db, COLLECTIONS.candidatures),
      where('status', '==', STATUSES.pending),
      orderBy('date', 'desc'),
      limit(limitCount)
    )
  );

  return snap.docs.map((doc) => {
    const data = doc.data() as any;
    return {
      id: doc.id,
      userId: data.userId,
      postId: data.postId,
      status: data.status,
      date: data.date?.toDate ? data.date.toDate() : undefined,
    };
  });
}

export async function fetchActivitiesToValidate(
  limitCount = 10
): Promise<ActivitySummary[]> {
  const snap = await getDocs(
    query(
      collection(db, COLLECTIONS.activities),
      where('status', '==', STATUSES.pending),
      orderBy('date', 'desc'),
      limit(limitCount)
    )
  );

  return snap.docs.map((doc) => {
    const data = doc.data() as any;
    return {
      id: doc.id,
      candidatureId: data.candidatureId,
      status: data.status,
      date: data.date?.toDate ? data.date.toDate() : undefined,
    };
  });
}

export async function fetchPaymentsToPay(
  limitCount = 10
): Promise<PaymentSummary[]> {
  const snap = await getDocs(
    query(
      collection(db, COLLECTIONS.payments),
      where('status', '!=', STATUSES.paid),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    )
  );

  return snap.docs.map((doc) => {
    const data = doc.data() as any;
    return {
      id: doc.id,
      activityId: data.activityId,
      status: data.status,
      amount: typeof data.amount === 'number' ? data.amount : undefined,
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : undefined,
    };
  });
}

